import React from "react";
import { FaArrowRight, FaCalendarAlt, FaClock, FaSpinner, FaTrophy } from "react-icons/fa";
import { useGetUpcomingIupcQuery } from "../../features/api/appApi";

const UpcomingIUPCPage = () => {
  const { data: upcomingContests = [], isLoading: loading } =
    useGetUpcomingIupcQuery();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return isNaN(date)
      ? "Invalid date"
      : date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
  };

  const daysRemaining = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffDays = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));

    if (diffDays > 7)
      return { text: `${diffDays} days remaining`, status: "open" };
    if (diffDays > 0) return { text: `${diffDays} days left`, status: "closing" };
    return { text: "Registration closed", status: "closed" };
  };

  return (
    <main className="home-shell min-h-screen px-4 pb-14 pt-28 sm:px-6">
      <section className="home-section border-none px-0 py-0">
        <div className="home-container">
          <div className="home-card-strong p-8 sm:p-10">
            <span className="section-kicker">IUPC / ICPC</span>
            <h1 className="home-title mt-4 text-3xl font-bold sm:text-5xl">
              Upcoming <span className="home-accent-gradient">Contests</span>
            </h1>
            <p className="mt-4 max-w-3xl text-sm sm:text-base home-muted">
              Prepare early and register on time for upcoming contests. Track deadlines and contest dates from one clean page.
            </p>
          </div>
        </div>
      </section>

      <section className="home-section border-none px-0 pt-8">
        <div className="home-container">
          {loading ? (
            <div className="home-card flex h-52 items-center justify-center">
              <FaSpinner className="animate-spin text-3xl home-accent" />
            </div>
          ) : (
            <div className="grid home-stagger-grid gap-4">
              {upcomingContests.map((contest) => {
                const remaining = daysRemaining(contest.registrationDeadline);
                return (
                  <article
                    key={contest.id}
                    className="home-card overflow-hidden p-6 sm:p-7"
                  >
                    <div className="mb-5 flex items-start justify-between gap-3">
                      <div>
                        <h2 className="text-2xl font-bold">{contest.name}</h2>
                        <p className="mt-2 text-sm home-muted">
                          {contest.description}
                        </p>
                      </div>
                      <FaTrophy className="mt-1 text-xl home-accent" />
                    </div>

                    <div className="grid home-stagger-grid gap-3 md:grid-cols-2">
                      <div className="home-card-strong p-4">
                        <p className="mb-1 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide home-muted">
                          <FaCalendarAlt className="home-accent" /> Contest Date
                        </p>
                        <p className="text-sm font-semibold">
                          {formatDate(contest.contestDate)}
                        </p>
                      </div>

                      <div className="home-card-strong p-4">
                        <p className="mb-1 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide home-muted">
                          <FaClock className="home-accent" /> Registration Deadline
                        </p>
                        <p className="text-sm font-semibold">
                          {formatDate(contest.registrationDeadline)}
                        </p>
                        <span
                          className="mt-2 inline-block rounded-full px-2.5 py-1 text-xs font-semibold"
                          style={{
                            background:
                              remaining.status === "closed"
                                ? "color-mix(in srgb, #ef4444 18%, var(--surface-soft))"
                                : "color-mix(in srgb, var(--brand) 22%, var(--surface-soft))",
                            color:
                              remaining.status === "closed"
                                ? "#b91c1c"
                                : "var(--brand-strong)",
                          }}
                        >
                          {remaining.text}
                        </span>
                      </div>
                    </div>

                    <a
                      href={contest.registrationLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="home-btn-primary mt-5 inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold"
                    >
                      Register Now <FaArrowRight className="text-xs" />
                    </a>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default UpcomingIUPCPage;
